import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { setAnalyticsScope } from '@/redux/features/analytics-scope/analytics-scope-slice'
import { useScopePortals } from '@/application/queries/use-scope-portals'
import { useSelectedEcommerceIds } from './use-selected-ecommerce-ids'

// useAnalyticsScope resolves the portals an analytics query covers. The list it returns is never
// empty on purpose: analytics-api refuses an empty scope, so "every portal" is expanded here.
export function useAnalyticsScope() {
  const dispatch = useAppDispatch()
  const { portals, loadingPortals } = useScopePortals()
  const selected = useAppSelector((s) => s.analyticsScope.selectedPortalIds)
  // Read straight from the store rather than through useEcommerceId(), which dispatches the first
  // portal when none is chosen and so would turn "All" into one portal on sight.
  const navbarPortal = useAppSelector((s) => s.generalData.selectedEcommerce)
  const navbarIds = useSelectedEcommerceIds()

  const ownedIds = useMemo(() => portals.map((p) => p.id), [portals])

  // A stored selection outlives the access that produced it, and one id outside the caller's
  // group fails the whole request, so it is held against what they own now.
  const selectedIds = useMemo(
    () => selected.filter((id) => ownedIds.includes(id)),
    [selected, ownedIds]
  )

  const setScope = useCallback(
    (ids: string[]) => dispatch(setAnalyticsScope(ids)),
    [dispatch]
  )

  // The navbar decides one portal, one group or everything; this scope refines a group or
  // everything. A group the portal list cannot resolve still covers every store in it.
  // Sorted so the same scope is the same request: it fixes the id the GET path carries and keeps
  // one React Query entry per scope rather than one per order the portals were ticked in.
  const ecommerceIds = useMemo(() => {
    if (navbarPortal) return [navbarPortal]
    const ids = selectedIds.length ? selectedIds : ownedIds
    if (!ids.length && !loadingPortals && navbarIds)
      return [...navbarIds].sort()
    return [...ids].sort()
  }, [navbarPortal, navbarIds, selectedIds, ownedIds, loadingPortals])

  return {
    ecommerceIds,
    ownedIds,
    selectedIds,
    isEveryPortal: selectedIds.length === 0,
    isGroupView: !navbarPortal,
    loadingScope: loadingPortals,
    setScope
  }
}
